// built-from: /finding-synthesis v1 impl:finding-synthesis role:implementation
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { davidMorgan } from '../fixtures/modernisation/archetypes';
import {
  businessSystemBoundaryProposal,
  emptyLumenGridFindings,
  lumenGridReviewedFindingEvidence,
} from '../fixtures/modernisation/findings';
import { useEngagement } from '../hooks/useEngagement';
import type {
  ActivityOutputVersion,
  DiscoveryPlan,
  DurableActivityOutput,
  DurableDiscoveryActivity,
} from '../hooks/useDiscoveryPlan';
import {
  useFindingSynthesis,
  type FindingClassification,
  type FindingConfidence,
  type FindingDraft,
  type FindingEvidenceRef,
  type FindingProposal,
  type FindingRecord,
  type UseFindingSynthesisReturn,
} from '../hooks/useFindingSynthesis';
import { ActivityOutputReview, type ActivityOutputReviewItem } from './ActivityOutputReview';

type Props = {
  discoveryPlan: DiscoveryPlan | null;
  discoveryRevision: string | null;
  onDiscoveryCommitted: () => void | Promise<void>;
};

type EvidenceRelationship = 'supports' | 'challenges' | '';
type EditableFindingProposal = Omit<FindingProposal, 'classification' | 'confidence'> & {
  classification: FindingClassification | '';
  confidence: FindingConfidence | '';
};
type ReviewedEvidence = {
  id: string;
  name: string;
  source: string;
  summary: string;
  activityName: string;
  ref: FindingEvidenceRef;
  output: ActivityOutputReviewItem;
  versions: ActivityOutputReviewItem[];
  suggestedRelationship: Exclude<EvidenceRelationship, ''> | null;
};

function commandId(prefix: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `${prefix}-${uuid ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
}

function reviewItem(
  activity: DurableDiscoveryActivity,
  output: DurableActivityOutput,
  version: ActivityOutputVersion,
): ActivityOutputReviewItem {
  return {
    id: output.id,
    activityId: activity.id,
    name: output.name,
    method: output.method,
    state: version.state,
    revision: version.version,
    returnedAt: version.returnedAt,
    reviewer: output.reviewer,
    contributors: output.contributors,
    sessionIds: [version.sessionId],
    summary: version.snapshot.payload.summary ?? 'Not captured',
    assumptions: version.snapshot.payload.assumptions ?? [],
    openQuestions: version.snapshot.payload.openQuestions ?? [],
  };
}

function planEvidence(plan: DiscoveryPlan | null): ReviewedEvidence[] {
  if (!plan) return [];
  return plan.activities.flatMap((activity) => (activity.outputs ?? []).flatMap((output) => {
    const versions = output.versions.map((version) => reviewItem(activity, output, version));
    return output.versions
      .filter((version) => version.state === 'reviewed')
      .map((version) => ({
        id: `${activity.id}/${output.id}/v${version.version}`,
        name: output.name,
        source: `${activity.definition.name} · Version ${version.version}`,
        summary: version.snapshot.payload.summary ?? 'Not captured',
        activityName: activity.definition.name,
        ref: { activityId: activity.id, outputId: output.id, version: version.version },
        output: reviewItem(activity, output, version),
        versions,
        suggestedRelationship: null,
      }));
  }));
}

function standaloneEvidence(): ReviewedEvidence[] {
  return lumenGridReviewedFindingEvidence.map((item) => ({
    id: item.id,
    name: item.name,
    source: item.source,
    summary: item.summary,
    activityName: item.source.split(' · ')[0],
    ref: item.ref,
    output: item.output,
    versions: [item.output],
    suggestedRelationship: item.relationship,
  }));
}

function evidenceKey(ref: FindingEvidenceRef): string {
  return `${ref.activityId}/${ref.outputId}/v${ref.version}`;
}

function CommandError({
  synthesis,
  onDiscoveryReload,
}: {
  synthesis: UseFindingSynthesisReturn;
  onDiscoveryReload: () => void | Promise<void>;
}): JSX.Element | null {
  if (!synthesis.error || synthesis.error.command === 'propose') return null;
  const discoveryConflict = synthesis.error.command === 'discovery' && synthesis.error.conflict;
  return (
    <div className="journey-output-notice" role="alert">
      <span>{synthesis.error.message}</span>
      {synthesis.error.conflict ? (
        <button
          className="journey-button journey-button--secondary"
          type="button"
          onClick={() => void (discoveryConflict ? onDiscoveryReload() : synthesis.loadLatestFindings())}
        >
          {discoveryConflict ? 'Load latest Discovery plan' : 'Load latest finding'}
        </button>
      ) : null}
    </div>
  );
}

function FindingsOverview({ synthesis, evidence }: { synthesis: UseFindingSynthesisReturn; evidence: ReviewedEvidence[] }): JSX.Element {
  const findings = synthesis.findings?.findings ?? [];
  const drafts = findings.filter((item) => item.status === 'draft');
  const confirmed = findings.filter((item) => item.status === 'confirmed');
  return (
    <div className="journey-page finding-page">
      <header className="journey-page__header"><span className="journey-eyebrow">Evidence-led conclusions</span><h1>Findings</h1><p>Turn reviewed evidence into traceable conclusions without hiding conflicts or remaining uncertainty.</p></header>
      <section className="journey-panel finding-work-list" aria-label="Finding work">
        <div className="finding-work-section">
          <div className="journey-section-heading"><div><span className="journey-eyebrow">Ready to synthesise</span><h2>Reviewed evidence</h2></div><span className="journey-count">{evidence.length} outputs</span></div>
          <div className="finding-work-row"><div><strong>{evidence.length ? 'Reviewed outputs are ready to synthesise' : 'No reviewed outputs are ready'}</strong><span>Select exact reviewed versions from completed Discovery activities.</span></div>{evidence.length >= 2 ? <Link className="journey-button journey-button--primary" to="/engagement/findings/synthesis/new">Start synthesis</Link> : null}</div>
        </div>
        <div className="finding-work-section">
          <div className="journey-section-heading"><div><span className="journey-eyebrow">Needs your judgement</span><h2>Draft findings</h2></div><span className="journey-count">{drafts.length}</span></div>
          {drafts.map((finding) => <FindingRow key={finding.id} finding={finding} evidence={evidence} />)}
          {!drafts.length ? <div className="journey-empty-state"><strong>No draft findings.</strong><span>AI proposals remain proposals until you create a draft.</span></div> : null}
        </div>
        <div className="finding-work-section">
          <div className="journey-section-heading"><div><span className="journey-eyebrow">Confirmed</span><h2>Findings ready for programme work</h2></div><span className="journey-count">{confirmed.length}</span></div>
          {confirmed.map((finding) => <FindingRow key={finding.id} finding={finding} evidence={evidence} />)}
          {!confirmed.length ? <div className="journey-empty-state"><strong>No findings are confirmed yet.</strong><span>Confirmed findings can drive later programme work.</span></div> : null}
        </div>
      </section>
    </div>
  );
}

function hasNewerEvidence(finding: FindingRecord, evidence: ReviewedEvidence[]): boolean {
  const refs = [...finding.draft.evidence.supports, ...finding.draft.evidence.challenges];
  return refs.some((ref) => evidence.some((item) => item.ref.activityId === ref.activityId
    && item.ref.outputId === ref.outputId && item.ref.version > ref.version));
}

function FindingRow({ finding, evidence }: { finding: FindingRecord; evidence: ReviewedEvidence[] }): JSX.Element {
  const needsEvidence = finding.draft.uncertainties.some((item) => item.blocking);
  return (
    <div className="finding-work-row">
      <div><strong>{finding.draft.title}</strong><span>{finding.status === 'confirmed' ? 'Confirmed' : needsEvidence ? 'Needs evidence' : `Draft · ${finding.draft.confidence} confidence`}</span>{hasNewerEvidence(finding, evidence) ? <span className="finding-newer-evidence">Newer evidence available</span> : null}</div>
      <Link className="journey-button journey-button--secondary" to={`/engagement/findings/${finding.id}`}>{finding.status === 'confirmed' ? 'Review traceability' : 'Open finding'}</Link>
    </div>
  );
}

function relationshipOf(proposal: FindingProposal, ref: FindingEvidenceRef): EvidenceRelationship {
  const key = evidenceKey(ref);
  if (proposal.evidence.supports.some((item) => evidenceKey(item) === key)) return 'supports';
  if (proposal.evidence.challenges.some((item) => evidenceKey(item) === key)) return 'challenges';
  return '';
}

function SynthesisWorkspace({ synthesis, evidence }: { synthesis: UseFindingSynthesisReturn; evidence: ReviewedEvidence[] }): JSX.Element {
  const navigate = useNavigate();
  const { currentEngagementId } = useEngagement();
  const [selected, setSelected] = useState<string[]>([]);
  const [editable, setEditable] = useState<EditableFindingProposal | null>(null);
  const [relationships, setRelationships] = useState<Record<string, EvidenceRelationship>>({});
  const createId = useRef(commandId('create-finding'));
  const selectedEvidence = evidence.filter((item) => selected.includes(item.id));

  const adoptProposal = (proposal: FindingProposal): void => {
    setEditable(proposal);
    setRelationships(Object.fromEntries(selectedEvidence.map((item) => [item.id, relationshipOf(proposal, item.ref)])));
  };
  useEffect(() => { if (synthesis.proposal) adoptProposal(synthesis.proposal); }, [synthesis.proposal]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestProposal = (): void => {
    if (!currentEngagementId) adoptProposal(businessSystemBoundaryProposal);
    void synthesis.proposeFinding(selectedEvidence.map((item) => item.ref)).then((proposal) => { if (proposal) adoptProposal(proposal); });
  };
  const manual = (): void => {
    setEditable({
      title: '', classification: '', statement: '', whyItMatters: '', confidence: '',
      confidenceRationale: '', evidence: { supports: [], challenges: [] }, uncertainties: [],
    });
    setRelationships(Object.fromEntries(selectedEvidence.map((item) => [item.id, ''])));
  };
  const selectedRelationships = (): FindingDraft['evidence'] => ({
    supports: selectedEvidence.filter((item) => relationships[item.id] === 'supports').map((item) => item.ref),
    challenges: selectedEvidence.filter((item) => relationships[item.id] === 'challenges').map((item) => item.ref),
  });
  const relationshipChoicesComplete = selectedEvidence.length >= 2 && selectedEvidence.every((item) => relationships[item.id]);
  const create = (): void => {
    if (!editable || !editable.classification || !editable.confidence || !relationshipChoicesComplete) return;
    const draft: FindingDraft = {
      findingId: 'business-system-boundary-misalignment',
      ...editable,
      classification: editable.classification,
      confidence: editable.confidence,
      evidence: selectedRelationships(),
    };
    void synthesis.createDraft(createId.current, new Date().toISOString(), davidMorgan, draft).then((id) => { if (id) navigate(`/engagement/findings/${id}`); });
  };
  const updateUncertainty = (index: number, value: string): void => {
    if (!editable) return;
    const uncertainties = editable.uncertainties.map((item, itemIndex) => itemIndex === index ? { ...item, statement: value } : item);
    setEditable({ ...editable, uncertainties });
  };

  return (
    <div className="journey-page finding-page">
      <nav className="journey-breadcrumb"><Link to="/engagement/findings">Findings</Link><span>/</span><span>New synthesis</span></nav>
      <header className="journey-page__header"><span className="journey-eyebrow">Consultant-controlled synthesis</span><h1>Synthesise reviewed evidence</h1><p>Select exact reviewed versions. AI can propose an interpretation; it cannot create or confirm a finding.</p></header>
      <div className="finding-synthesis-grid">
        <section className="journey-panel" aria-labelledby="finding-evidence-title">
          <div className="journey-section-heading"><div><span className="journey-eyebrow">Reviewed versions only</span><h2 id="finding-evidence-title">Evidence</h2></div><span className="journey-count">{selected.length} selected</span></div>
          <div className="finding-evidence-select">
            {evidence.map((item) => (
              <div key={item.id} className="finding-evidence-option">
                <input aria-label={`Select ${item.name}`} type="checkbox" checked={selected.includes(item.id)} onChange={(event) => setSelected((current) => event.target.checked ? [...current, item.id] : current.filter((id) => id !== item.id))} />
                <span><strong>{item.name}</strong><small>{item.source}</small><p>{item.summary}</p></span>
                <Link to={`/engagement/findings/evidence/${encodeURIComponent(item.ref.activityId)}/outputs/${encodeURIComponent(item.ref.outputId)}/versions/${item.ref.version}`} aria-label={`Open evidence for ${item.name}`}>Open evidence</Link>
              </div>
            ))}
          </div>
          <button className="journey-button journey-button--primary" type="button" disabled={selected.length < 2 || synthesis.pending === 'propose'} onClick={requestProposal}>{synthesis.pending === 'propose' ? 'Proposing a finding…' : 'Propose a finding'}</button>
        </section>
        <section className="journey-panel finding-proposal" aria-labelledby="finding-proposal-title">
          <span className="journey-eyebrow">AI proposal Â· not a finding</span><h2 id="finding-proposal-title">Proposed interpretation</h2>
          {synthesis.error?.command === 'propose' && !editable ? (
            <div className="journey-empty-state" role="alert"><strong>Proposal unavailable</strong><span>{synthesis.error.message} Your evidence selection has been kept.</span><div className="finding-proposal-actions"><button className="journey-button journey-button--secondary" type="button" onClick={requestProposal}>Retry</button><button className="journey-button journey-button--primary" type="button" onClick={manual}>Write finding manually</button></div></div>
          ) : editable ? (
            <>
              <label>Finding title<input value={editable.title} onChange={(event) => setEditable({ ...editable, title: event.target.value })} /></label>
              <label>Finding statement<textarea value={editable.statement} onChange={(event) => setEditable({ ...editable, statement: event.target.value })} /></label>
              <label>Classification<select value={editable.classification} onChange={(event) => setEditable({ ...editable, classification: event.target.value as EditableFindingProposal['classification'] })}><option value="">Choose classification</option><option value="problem">Problem</option><option value="cause">Cause</option><option value="risk">Risk</option><option value="opportunity">Opportunity</option></select></label>
              <label>Why it matters<textarea value={editable.whyItMatters} onChange={(event) => setEditable({ ...editable, whyItMatters: event.target.value })} /></label>
              {selectedEvidence.map((item) => <label key={item.id}>Relationship for {item.name}<select value={relationships[item.id] ?? ''} onChange={(event) => setRelationships((current) => ({ ...current, [item.id]: event.target.value as EvidenceRelationship }))}><option value="">Choose relationship</option><option value="supports">Supports</option><option value="challenges">Challenges or limits</option></select></label>)}
              <label>Confidence<select value={editable.confidence} onChange={(event) => setEditable({ ...editable, confidence: event.target.value as EditableFindingProposal['confidence'] })}><option value="">Choose confidence</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></label>
              <label>Confidence rationale<textarea value={editable.confidenceRationale} onChange={(event) => setEditable({ ...editable, confidenceRationale: event.target.value })} /></label>
              {editable.uncertainties.map((item, index) => <label key={item.id}>Uncertainty {index + 1}<textarea value={item.statement} onChange={(event) => updateUncertainty(index, event.target.value)} /></label>)}
              <button className="journey-button journey-button--secondary" type="button" onClick={() => setEditable({ ...editable, uncertainties: [...editable.uncertainties, { id: commandId('uncertainty'), statement: '', blocking: false }] })}>Add uncertainty</button>
              <div className="finding-proposal-actions"><button className="journey-button journey-button--secondary" type="button" onClick={() => { synthesis.clearProposal(); setEditable(null); }}>Discard proposal</button><button className="journey-button journey-button--primary" type="button" disabled={!editable.title.trim() || !editable.statement.trim() || !editable.classification || !editable.confidence || !editable.confidenceRationale.trim() || !relationshipChoicesComplete || synthesis.pending === 'create'} onClick={create}>Create draft finding</button></div>
            </>
          ) : <div className="journey-empty-state"><strong>No proposal yet.</strong><span>Select at least two reviewed versions, then ask for a proposed interpretation.</span></div>}
        </section>
      </div>
    </div>
  );
}

function FindingDetail({ synthesis, finding, evidence, discoveryPlan, discoveryRevision, onDiscoveryCommitted }: Props & { synthesis: UseFindingSynthesisReturn; finding: FindingRecord; evidence: ReviewedEvidence[] }): JSX.Element {
  const navigate = useNavigate();
  const updateId = useRef(commandId('update-finding'));
  const confirmId = useRef(commandId('confirm-finding'));
  const proposalId = useRef(commandId('propose-discovery'));
  const uncertainty = finding.draft.uncertainties[0];
  const needsEvidence = finding.draft.uncertainties.some((item) => item.blocking);
  const discoveryProposed = Boolean(uncertainty && discoveryPlan?.scopeDecisions.some((decision) => decision.sourceFindingId === finding.id && decision.sourceUncertaintyId === uncertainty.id));
  const linkedEvidence = (refs: FindingEvidenceRef[]) => refs.map((ref) => evidence.find((item) => evidenceKey(item.ref) === evidenceKey(ref))).filter((item): item is ReviewedEvidence => Boolean(item));
  const block = (): void => { if (uncertainty) void synthesis.updateDraft(finding.id, updateId.current, { uncertainties: finding.draft.uncertainties.map((item) => item.id === uncertainty.id ? { ...item, blocking: true } : item) }); };
  const proposeDiscovery = (): void => { if (uncertainty) void synthesis.proposeDiscovery(finding.id, uncertainty.id, proposalId.current, discoveryRevision, 'reconcile-domain-perspectives', new Date().toISOString()).then((saved) => { if (saved) void onDiscoveryCommitted(); }); };
  return (
    <div className="journey-page finding-page">
      <nav className="journey-breadcrumb"><Link to="/engagement/findings">Findings</Link><span>/</span><span>{finding.draft.title}</span></nav>
      <CommandError synthesis={synthesis} onDiscoveryReload={onDiscoveryCommitted} />
      <header className="journey-page__header journey-page__header--action">
        <div><div className="journey-title-line"><span className="journey-eyebrow">{finding.draft.classification} finding</span><span className={`journey-status journey-status--${finding.status === 'confirmed' ? 'complete' : 'awaiting-review'}`}>{finding.status === 'confirmed' ? 'Confirmed' : needsEvidence ? 'Needs evidence' : 'Draft'}</span></div><h1>{finding.draft.title}</h1><p>{finding.draft.statement}</p>{hasNewerEvidence(finding, evidence) ? <span className="finding-newer-evidence">Newer evidence available</span> : null}</div>
        {finding.status === 'draft' && !needsEvidence ? <button className="journey-button journey-button--primary journey-button--large" type="button" onClick={() => void synthesis.confirmFinding(finding.id, confirmId.current, new Date().toISOString(), davidMorgan)}>Confirm finding</button> : null}
        {needsEvidence && !discoveryProposed ? <button className="journey-button journey-button--primary journey-button--large" type="button" onClick={proposeDiscovery}>Propose Discovery activity</button> : null}
        {discoveryProposed ? <button className="journey-button journey-button--primary journey-button--large" type="button" onClick={() => navigate('/engagement/discovery#scope-decisions')}>Review in Discovery</button> : null}
      </header>
      <div className="finding-detail-grid"><main className="finding-detail-main"><section className="journey-panel"><span className="journey-eyebrow">Why it matters</span><h2>Change crosses business and software ownership</h2><p>{finding.draft.whyItMatters}</p></section><EvidenceList title="Supports this finding" items={linkedEvidence(finding.draft.evidence.supports)} /><EvidenceList title="Challenges or limits this finding" items={linkedEvidence(finding.draft.evidence.challenges)} /></main><aside className="finding-detail-aside"><section className="journey-panel"><span className="journey-eyebrow">Confidence</span><h2>{finding.draft.confidence}</h2><p>{finding.draft.confidenceRationale}</p></section>{uncertainty ? <section className="journey-panel"><span className="journey-eyebrow">Remaining uncertainty</span><h2>Primary source of coupling</h2><p>{uncertainty.statement}</p>{finding.status === 'draft' && !uncertainty.blocking ? <button className="journey-button journey-button--secondary" type="button" onClick={block}>This blocks confirmation</button> : null}{uncertainty.blocking ? <span className="finding-blocking-label">Blocking confirmation</span> : null}</section> : null}{discoveryProposed ? <section className="journey-panel finding-success" role="status"><strong>Discovery work proposed</strong><span>The linked activity is waiting for a scope decision.</span></section> : null}</aside></div>
    </div>
  );
}

function EvidenceList({ title, items }: { title: string; items: ReviewedEvidence[] }): JSX.Element {
  return <section className="journey-panel"><span className="journey-eyebrow">Traceable reviewed versions</span><h2>{title}</h2>{items.map((item) => <article className="finding-evidence-row" key={item.id}><div><strong>{item.name}</strong><span>{item.source}</span><p>{item.summary}</p></div><Link to={`/engagement/findings/evidence/${encodeURIComponent(item.ref.activityId)}/outputs/${encodeURIComponent(item.ref.outputId)}/versions/${item.ref.version}`}>Open exact version</Link></article>)}</section>;
}

function EvidenceReview({ evidence }: { evidence: ReviewedEvidence[] }): JSX.Element {
  const { activityId, outputId, version } = useParams();
  const selected = evidence.find((item) => item.ref.activityId === activityId && item.ref.outputId === outputId && item.ref.version === Number(version));
  if (!selected) return <Navigate to="/engagement/findings" replace />;
  return <ActivityOutputReview output={selected.output} versions={selected.versions} activityPath="/engagement/findings/synthesis/new" activityName={selected.activityName} onCorrectAtSource={() => undefined} onMarkReviewed={() => undefined} />;
}

export function FindingSynthesis({ discoveryPlan, discoveryRevision, onDiscoveryCommitted }: Props): JSX.Element {
  const { currentEngagementId } = useEngagement();
  const synthesis = useFindingSynthesis(emptyLumenGridFindings, businessSystemBoundaryProposal);
  const derived = useMemo(() => planEvidence(discoveryPlan), [discoveryPlan]);
  const evidence = useMemo(() => (derived.length || currentEngagementId ? derived : standaloneEvidence()), [currentEngagementId, derived]);
  const records = useMemo(() => synthesis.findings?.findings ?? [], [synthesis.findings]);
  return (
    <Routes>
      <Route index element={<FindingsOverview synthesis={synthesis} evidence={evidence} />} />
      <Route path="synthesis/new" element={<SynthesisWorkspace synthesis={synthesis} evidence={evidence} />} />
      <Route path="evidence/:activityId/outputs/:outputId/versions/:version" element={<EvidenceReview evidence={evidence} />} />
      {records.map((finding) => <Route key={finding.id} path={finding.id} element={<FindingDetail synthesis={synthesis} finding={finding} evidence={evidence} discoveryPlan={discoveryPlan} discoveryRevision={discoveryRevision} onDiscoveryCommitted={onDiscoveryCommitted} />} />)}
      <Route path="*" element={synthesis.loading ? <div className="journey-page"><p>Loading findings…</p></div> : <Navigate to="/engagement/findings" replace />} />
    </Routes>
  );
}
